import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { EnterpriseEventRequestPayload } from './enterprise-event-client';

declare global {
  interface Window {
    optimizely: any;
    FS: any;
    utag_data: any;
    opera: any;
  }
}

export const PROSPECT_ID_COOKIE_NAME = 'prospect__id';
export const PROSPECT_SESSION_ID_COOKIE_NAME = 'prospect__session_id';
export const GOOGLE_CLICK_ID_COOKIE_NAME = 'gclid_cookie';
export const FACEBOOK_BROWSER_ID_COOKIE_NAME = '_fbp';
export const FIRST_VISITED_URL_COOKIE_NAME = 'AdobeFirstURLVisited';
export const MS_CLICK_ID_COOKIE_NAME = '_uetmsclkid';

export const UTM_CAMPAIGN_KEY = 'utm_campaign';
export const UTM_CONTENT_KEY = 'utm_content';
export const UTM_MEDIUM_KEY = 'utm_medium';
export const UTM_SOURCE_KEY = 'utm_source';
export const UTM_TERM_KEY = 'utm_term';
export const CID_KEY = 'cid';
export const CAMPAIGN_PARTICIPANT_CODE_KEY = 'cpid';

const EXCLUDE_PROSPECT_SESSION_ID_EVENTS = ['com.bestegg.dpid.create', 'com.bestegg.dpid.visited'];

export enum EnterpriseEventEndPoint {
  SBX = 'https://eev.sbx.bestegg.com/eev/publish/v2',
  UAT = 'https://eev.uat.bestegg.com/eev/publish/v2',
  PRD = 'https://eev.bestegg.com/eev/publish/v2',
}

export enum EchoEndpoint {
  SBX = 'https://eev.sbx.bestegg.com/utils/echo',
  UAT = 'https://eev.uat.bestegg.com/utils/echo',
  PRD = 'https://eev.bestegg.com/utils/echo',
}

export class EnterpriseEventClientHelper {
  private window: Window & typeof globalThis;

  /**
   * @param windowOverride an instance of the global `window` object for use in providing a mock
   * window for unit testing. If the browser's global `window` object will be used
   */
  constructor(windowOverride?: Window & typeof globalThis) {
    this.window = windowOverride || window;
  }

  mapEnterpriseEvent(event: EnterpriseEventRequestPayload, clientIpAddress?: string) {
    const dpid = Cookies.get(PROSPECT_ID_COOKIE_NAME);

    if (!dpid || dpid === 'undefined') {
      throw new Error('Cannot map event: prospect__id cookie has not been set');
    }

    const { type, source, subject, parentsubject, metadata, parentrequired, session, ...data } = event;
    const definedData = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== null && value !== undefined && value !== '')
    );

    const urlObject = new URL(this.window.location.href);
    const urlQueryString = urlObject.search.slice(1) || undefined;
    const { searchParams } = urlObject;
    const utmCampaign = this.getFromSearchParamsOrCookie(searchParams, UTM_CAMPAIGN_KEY);
    const utmContent = this.getFromSearchParamsOrCookie(searchParams, UTM_CONTENT_KEY);
    const utmMedium = this.getFromSearchParamsOrCookie(searchParams, UTM_MEDIUM_KEY);
    const utmSource = this.getFromSearchParamsOrCookie(searchParams, UTM_SOURCE_KEY);
    const utmTerm = this.getFromSearchParamsOrCookie(searchParams, UTM_TERM_KEY);
    const cid = this.getFromSearchParamsOrCookie(searchParams, CID_KEY);
    const campaignParticipantCode = this.getFromSearchParamsOrCookie(searchParams, CAMPAIGN_PARTICIPANT_CODE_KEY);
    const googleClickId = Cookies.get(GOOGLE_CLICK_ID_COOKIE_NAME);
    const facebookBrowserId = Cookies.get(FACEBOOK_BROWSER_ID_COOKIE_NAME);
    const msClickId = Cookies.get(MS_CLICK_ID_COOKIE_NAME)?.split('_uet').pop(); // remove the prefix '_uet' from the value
    const pageReferrer = document.referrer || undefined;
    const firstVisitedUrl = Cookies.get(FIRST_VISITED_URL_COOKIE_NAME);
    const fullStoryUrl = (window.FS?.getCurrentSessionURL && window.FS.getCurrentSessionURL()) || undefined;
    const hashedEmailAddress = (window.utag_data && window.utag_data['cp.utag_main_persist_hashed_email']) || undefined;

    const payload: any = {
      datacontenttype: 'application/json',
      data: {
        dpid,
        userAgent: window.navigator.userAgent,
        url: this.window.location.origin + this.window.location.pathname,
        userDeviceType: this.getDeviceType(),
        urlHostname: this.window.location.hostname,
        urlPathname: this.window.location.pathname,
        fullUrl: this.window.location.href,
        utmCampaign,
        utmContent,
        utmMedium,
        utmSource,
        utmTerm,
        cid,
        campaignParticipantCode,
        optimizelyExperimentNames: this.window.utag_data?.optimizely_experiment_names || undefined,
        optimizelyVariationNames: this.window.utag_data?.optimizely_variation_names || undefined,
        googleClickId,
        msClickId,
        facebookBrowserId,
        pageReferrer,
        urlQueryString,
        firstVisitedUrl,
        fullStoryUrl,
        hashedEmailAddress,
        clientIpAddress,
        ...definedData,
      },
      metadata,
      source,
      type,
      subject,
      parentsubject,
      time: new Date().toISOString(),
      eventpublishedtime: new Date().toISOString(),
      id: uuidv4(),
      specversion: '1.0',
      eespecversion: '2.0',
      parentrequired,
    };

    // Since DPID created/visited are parents of the prospect session, the session ID should not
    // be added to it
    if (!EXCLUDE_PROSPECT_SESSION_ID_EVENTS.includes(event.type)) {
      const prospectSessionId = Cookies.get(PROSPECT_SESSION_ID_COOKIE_NAME);

      if (!prospectSessionId || prospectSessionId === 'undefined') {
        throw new Error('Cannot map event: prospect__session_id cookie has not been set');
      }

      payload.data.prospectsessionid = prospectSessionId;
    }

    if (session) {
      if (session.sessionName && session.sessionValue) {
        const sessionKey = session.sessionName;
        payload.data[sessionKey] = session.sessionValue;
        return payload;
      } else {
        console.error('EEV2 Error, missing session name or value');
        return;
      }
    } else {
      return payload;
    }
  }

  // This method mirrors the Tealium iQ browser detection
  private getDeviceType() {
    // Mobile detection function
    var device_mobile = (function (a: string): boolean {
      var mobilePatternsLong =
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i;
      const mobilePatternsShort =
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;

      return mobilePatternsLong.test(a) || mobilePatternsShort.test(a.substr(0, 4));
    })(navigator.userAgent || navigator.vendor || window.opera || '');

    // Tablet detection function
    const device_tablet = (function (a: string): boolean {
      const tabletPattern = /ipad|android.+\d safari|tablet/i;
      return tabletPattern.test(a);
    })(navigator.userAgent || navigator.vendor || window.opera || '');

    var DeviceType;

    if (device_mobile) {
      DeviceType = 'Mobile';
    } else if (device_tablet) {
      DeviceType = 'Tablet';
    } else {
      DeviceType = 'Desktop';
    }

    return DeviceType;
  }

  private getFromSearchParamsOrCookie(searchParams: URLSearchParams, key: string) {
    return searchParams.get(key) || Cookies.get(key);
  }
}
